// src/components/Leader/LeaderFiltersBar.tsx
import React, { useMemo } from 'react';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import { OrgChartRow, getDirectReports, isManagerEmail } from '../../support/orgScope';
import { asSingleValue } from '../../support/utils';
import type { LeaderTab } from '../../support/leaderPages';

export type LeaderMode = 'team' | 'individual';
export type { LeaderDomain } from '../../support/leaderPages';

const TEAM_VALUE = '__team__';

export interface LeaderNavState {
  path: string[];
  mode: LeaderMode;
  // Domain of the active tab. Typed as `string` because the tab set is DB-driven (leader_page rows);
  // the known domains are `LeaderDomain`.
  domain: string;
}

interface Props {
  orgRows: OrgChartRow[];
  state: LeaderNavState;
  onChange: (next: LeaderNavState) => void;
  // Domain tabs derived from the leader_page rows (or the built-in defaults). See support/leaderPages.
  tabs: LeaderTab[];
}

function displayNameForEmail(_rows: OrgChartRow[], email: string): string {
  return email;
}

function optionLabel(rows: OrgChartRow[], r: OrgChartRow) {
  const email = String(r.user_email).toLowerCase();
  const base = displayNameForEmail(rows, email);
  return r.is_manager ? `${base} (Mgr)` : base;
}

function TabButton({
                     active,
                     children,
                     onClick,
                   }: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-1 rounded-md border text-sm font-semibold',
        active
          ? 'bg-white border-gray-300 text-gray-900'
          : 'bg-transparent border-transparent text-gray-600 hover:text-gray-900 hover:bg-white',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export default function LeaderFiltersBar({ orgRows, state, onChange, tabs }: Props) {
  const path = state.path.map((e) => e.toLowerCase());

  const selects = useMemo(() => {
    const out: { managerEmail: string; reports: OrgChartRow[]; selectedEmail: string | null }[] = [];
    for (let level = 0; level < path.length; level++) {
      const managerEmail = path[level];
      if (!isManagerEmail(orgRows, managerEmail)) break;

      const reports = getDirectReports(orgRows, managerEmail);
      if (!reports.length) break;

      const selectedEmail = path[level + 1] ?? null;
      out.push({ managerEmail, reports, selectedEmail });
    }
    return out;
  }, [orgRows, path]);

  const handleLevelChange = (levelIdx: number, value: string) => {
    const managerEmail = path[levelIdx];

    if (value === TEAM_VALUE) {
      const nextPath = path.slice(0, levelIdx + 1);
      onChange({ ...state, path: nextPath, mode: 'team' });
      return;
    }

    const nextPath = [...path.slice(0, levelIdx + 1), value.toLowerCase()];
    const nextIsMgr = isManagerEmail(orgRows, value);

    onChange({
      ...state,
      path: nextPath,
      mode: nextIsMgr ? 'team' : 'individual',
    });
  };

  return (
    <div className="flex flex-col gap-2 bg-gray-100 border border-gray-200 rounded-md p-3">
      {/* Domain Tabs */}
      <div className="flex gap-2 items-center">
        <div className="hbr-css__text-primary-xs-semibold mr-2">Data Source</div>
        <div className="flex gap-1 bg-gray-200 rounded-md p-1">
          {tabs.map((tab) => (
            <TabButton
              key={tab.domain}
              active={state.domain === tab.domain}
              onClick={() => onChange({ ...state, domain: tab.domain })}
            >
              {tab.label}
            </TabButton>
          ))}
        </div>
      </div>

      {/* Org navigation */}
      <div className="flex gap-3 flex-wrap items-end">
        {selects.map((sel, idx) => {
          const currentValue = sel.selectedEmail ?? TEAM_VALUE;

          return (
            <div key={sel.managerEmail} className="min-w-[280px]">
              <div className="hbr-css__text-primary-xs-semibold mb-1">
                {idx === 0 ? 'Leader View' : `Sub-team`}
              </div>

              <HbrSelect value={currentValue} onHbr-change={(e) => handleLevelChange(idx, asSingleValue(e.target.value))}>
                <HbrOption value={TEAM_VALUE}>Team Summary</HbrOption>
                <HbrOption disabled value="__divider__">
                  ─────────
                </HbrOption>

                {sel.reports.map((r) => {
                  const email = String(r.user_email).toLowerCase();
                  return (
                    <HbrOption key={email} value={email}>
                      {optionLabel(orgRows, r)}
                    </HbrOption>
                  );
                })}
              </HbrSelect>
            </div>
          );
        })}
      </div>
    </div>
  );
}